"use client";

import { useEffect, useRef } from "react";
import { useLanguage } from "@/contexts/LanguageContext";

const Hero = () => {
  const { translations, currentLang } = useLanguage();
  const t = translations[currentLang];
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const particlesArrayRef = useRef<any[]>([]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;

    const numParticles = 120;

    class Particle {
      x: number;
      y: number;
      dx: number;
      dy: number;
      size: number;

      constructor(x: number, y: number, dx: number, dy: number, size: number) {
        this.x = x;
        this.y = y;
        this.dx = dx;
        this.dy = dy;
        this.size = size;
      }

      draw() {
        if (!ctx) return;
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2, false);
        ctx.fillStyle = "#00c4ff";
        ctx.fill();
      }

      update() {
        if (!canvas) return;
        if (this.x > canvas.width || this.x < 0) this.dx = -this.dx;
        if (this.y > canvas.height || this.y < 0) this.dy = -this.dy;
        this.x += this.dx;
        this.y += this.dy;
        this.draw();
      }
    }

    const init = () => {
      particlesArrayRef.current = [];
      for (let i = 0; i < numParticles; i++) {
        const size = Math.random() * 2 + 1;
        const x = Math.random() * canvas.width;
        const y = Math.random() * canvas.height;
        const dx = (Math.random() - 0.5) * 1.5;
        const dy = (Math.random() - 0.5) * 1.5;
        particlesArrayRef.current.push(new Particle(x, y, dx, dy, size));
      }
    };

    const connect = () => {
      for (let a = 0; a < particlesArrayRef.current.length; a++) {
        for (let b = a; b < particlesArrayRef.current.length; b++) {
          const distX =
            particlesArrayRef.current[a].x - particlesArrayRef.current[b].x;
          const distY =
            particlesArrayRef.current[a].y - particlesArrayRef.current[b].y;
          const distance = Math.sqrt(distX * distX + distY * distY);

          if (distance < 120) {
            ctx.beginPath();
            ctx.strokeStyle = `rgba(0, 196, 255, ${1 - distance / 120})`;
            ctx.lineWidth = 0.6;
            ctx.moveTo(
              particlesArrayRef.current[a].x,
              particlesArrayRef.current[a].y
            );
            ctx.lineTo(
              particlesArrayRef.current[b].x,
              particlesArrayRef.current[b].y
            );
            ctx.stroke();
          }
        }
      }
    };

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      for (let i = 0; i < particlesArrayRef.current.length; i++) {
        particlesArrayRef.current[i].update();
      }
      connect();
      requestAnimationFrame(animate);
    };

    const handleResize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
      init();
    };

    init();
    animate();

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  return (
    <section className="w-full grow relative bg-black min-h-[400px]">
      <canvas ref={canvasRef} className="absolute inset-0"></canvas>
      <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 flex flex-col md:flex-row items-center justify-center text-center z-10 gap-4 md:gap-10">
        <span className="text-xl md:text-5xl lg:text-[50px] whitespace-nowrap font-bold text-gray-400 w-[130px] md:w-[130px] text-center flex-shrink-0">
          {/* {t.subLeft} */}
          <span className="block">CDL</span>
          <span className="block text-sm text-gray-400">
            Core Deep Learning
          </span>
        </span>
        <h1 className="text-4xl md:text-6xl lg:text-[100px] font-bold leading-tight whitespace-nowrap">
          <strong className="text-[red]">A</strong>rtificial
          <br />
          <strong className="text-[red]">I</strong>ntelligence
        </h1>
        <span className="text-xl md:text-5xl lg:text-[50px] whitespace-nowrap font-bold text-gray-400 w-20 md:w-30 text-center flex-shrink-0">
          {t.subRight}
        </span>
      </div>
      <p className="absolute top-1/2 left-1/2 transform -translate-x-1/2 text-lg md:text-xl lg:text-[30px] opacity-85 text-center z-10 w-full max-w-4xl mt-32 lg:mt-40 px-4">
        {t.desc}
      </p>
    </section>
  );
};

export default Hero;
